"use client"

import { Field, ErrorMessage } from "formik"
import * as Yup from "yup"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import Select from "react-select"
import { formatNumberWithCommas, processDecimalInput } from "../../utils/formatNumber"
import { currencyOptions } from "../../utils/currencyOptions"
import { formatLocalDate, parseLocalDate } from "../../utils/dateUtils"

export const liabilityInitialValues = {
  liabilityName: "",
  category: "",
  status: "",
  notes: "",
  currency: "",
  originalAmount: "",
  outstandingAmount: "",
  interestRate: "",
  createdDate: "",
  lastUpdated: "",
}

export const liabilityValidationSchema = Yup.object().shape({
  liabilityName: Yup.string()
      .min(3, "Min length: 3 characters")
      .max(50, "Max length: 50 characters")
      .matches(
        /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
        "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
      )
      .required("Name is required"),          
  category: Yup.string().min(3, "Min length: 3 characters")
      .max(50, "Max length: 50 characters")
      .matches(
        /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
        "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
      )
      .required("Category is required"), 
  status: Yup.string().required("Status is required"),
  notes: Yup.string(),
  originalAmount: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .typeError("Must be a number")
    .min(0, "Must be 0 or greater"),
  outstandingAmount: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .typeError("Must be a number")
    .min(0, "Must be 0 or greater")
    .test(
      "outstanding-vs-original-amount",
      "Outstanding amount cannot be greater than the original amount.",
      function (value) {
        const { originalAmount } = this.parent
        if (value === undefined || value === null || originalAmount === undefined || originalAmount === null) return true
        return Number(value) <= Number(originalAmount)
      }
    ),
  interestRate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .typeError("Must be a number")
    .min(0, "Must be 0 or greater")
    .max(100, "Must be between 0 and 100"),

  createdDate: Yup.string()
    .required("Created date is required")
    .test(
      "created-before-lastUpdated",
      "Created date cannot be after Last Updated date",
      function (value) {
        const { lastUpdated } = this.parent
        if (!value || !lastUpdated) return true
        return new Date(value) <= new Date(lastUpdated)
      }
    ),
  lastUpdated: Yup.string()
    .required("Last updated is required")
    .test(
      "lastUpdated-after-created",
      "Last updated date cannot be before Created date",
      function (value) {
        const { createdDate } = this.parent
        if (!value || !createdDate) return true
        return new Date(value) >= new Date(createdDate)
      }
    ),

})

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "paidOff", label: "Paid Off" },
  { value: "defaulted", label: "Defaulted" },
]

const PersonalDebtsForm = ({ values, setFieldValue, errors, touched, localReadOnly }) => {
  return (
    <div className="row">
      {/* Liability Name */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Liability Name/Description<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="liabilityName"
          placeholder="e.g., Student Loan, Credit Card Debt"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.liabilityName && touched.liabilityName ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="liabilityName" component="div" className="invalid-feedback" />
      </div>

      {/* Category */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Category<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="category"
          placeholder="e.g., Student Loan, Credit Card, Personal Loan"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.category && touched.category ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="category" component="div" className="invalid-feedback" />
      </div>

      {/* Status */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Status<span className="text-danger">*</span>
        </label>
        <Select
          options={statusOptions}
          value={statusOptions.find(option => option.value === values.status)}
          onChange={option => setFieldValue("status", option.value)}
          className="react-select-container"
          classNamePrefix="react-select"
         readOnly={localReadOnly}
             isDisabled={localReadOnly}
        />
        <ErrorMessage name="status" component="div" className="text-danger small" />
      </div>

      {/* Original Amount */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Personal Debt Amount</label>
        <div className="input-group">
          <Select
            options={currencyOptions}
            value={currencyOptions.find(option => option.value === values.currency)}
            onChange={option => setFieldValue("currency", option.value)}
            className="currency-select"
            classNamePrefix="react-select"
            isDisabled={localReadOnly}
            styles={{ container: base => ({ ...base, minWidth: 120 }) }}
          />
          <Field
            type="text"
            name="originalAmount"
            placeholder="e.g., 50,000"
            readOnly={localReadOnly}
            isDisabled={localReadOnly}
            className={`form-control ${errors.originalAmount && touched.originalAmount ? "is-invalid" : ""}`}
            value={values.originalAmount ? formatNumberWithCommas(values.originalAmount) : ""}
            onChange={(e) => {
              const unformatted = processDecimalInput(e.target.value)
              setFieldValue("originalAmount", unformatted || "", true)
              setTimeout(() => {
                setFieldValue("outstandingAmount", values.outstandingAmount, true)
              }, 0)
            }}
            onPaste={(e) => {
              e.preventDefault()
              const unformatted = processDecimalInput(e.clipboardData.getData("text"))
              setFieldValue("originalAmount", unformatted || "", true)
              setTimeout(() => {
                setFieldValue("outstandingAmount", values.outstandingAmount, true)
              }, 0)
            }}
            onBlur={(e) => {
              const unformatted = e.target.value.replace(/,/g, "")
              if (unformatted) {
                setFieldValue("originalAmount", unformatted)
              }
            }}
          />
        </div>
        <small className="text-muted d-block mt-1">Initial / total borrowed amount</small>
        <ErrorMessage name="originalAmount" component="div" className="invalid-feedback" />
      </div>

      {/* Outstanding Amount */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Outstanding Amount</label>
        <Field
          type="text"
          name="outstandingAmount"
          placeholder="e.g., 15,000"
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
          className={`form-control ${errors.outstandingAmount && touched.outstandingAmount ? "is-invalid" : ""}`}
          value={values.outstandingAmount ? formatNumberWithCommas(values.outstandingAmount) : ""}
          onChange={(e) => {
            let unformatted = e.target.value.replace(/[^\d.]/g, "")
            const parts = unformatted.split(".")
            if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
            if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
            setFieldValue("outstandingAmount", unformatted || "")
          }}
          onBlur={(e) => {
            const unformatted = e.target.value.replace(/,/g, "")
            if (unformatted) {
              setFieldValue("outstandingAmount", unformatted)
            }
          }}
        />
        <small className="text-muted d-block mt-1">Current remaining balance (counts toward Net Worth)</small>
        <ErrorMessage name="outstandingAmount" component="div" className="invalid-feedback" />
      </div>

      {/* Interest Rate */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Interest Rate (%)</label>
        <Field
          type="number"
          name="interestRate"
          placeholder="e.g., 5.5"
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
          className={`form-control ${errors.interestRate && touched.interestRate ? "is-invalid" : ""}`}
          step="0.01"
        />
        <ErrorMessage name="interestRate" component="div" className="invalid-feedback" />
      </div>

      {/* Notes */}
      {/* <div className="col-md-6 mb-4">
        <label className="form-label">Notes</label>
        <Field
          as="textarea"
          name="notes"
          placeholder="Additional details about the liability"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.notes && touched.notes ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="notes" component="div" className="invalid-feedback" />
      </div> */}

      {/* Created Date */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Created Date<span className="text-danger">*</span>
        </label>
        <Field name="createdDate">
          {({ field, form }) => (
            <DatePicker
              selected={parseLocalDate(field.value)}
              onChange={date => form.setFieldValue("createdDate", date ? formatLocalDate(date) : "")}
              dateFormat="yyyy-MM-dd"
              className={`form-control ${errors.createdDate && touched.createdDate ? "is-invalid" : ""}`}
              placeholderText="Select date"
              autoComplete="off"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              readOnly={localReadOnly}
              isDisabled={localReadOnly}
            />
          )}
        </Field>
        <ErrorMessage name="createdDate" component="div" className="text-danger small" />
      </div>

      {/* Last Updated */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Last Updated<span className="text-danger">*</span>
        </label>
        <Field name="lastUpdated">
          {({ field, form }) => (
            <DatePicker
              selected={parseLocalDate(field.value)}
              onChange={date => form.setFieldValue("lastUpdated", date ? formatLocalDate(date) : "")}
              dateFormat="yyyy-MM-dd"
              className={`form-control ${errors.lastUpdated && touched.lastUpdated ? "is-invalid" : ""}`}
              placeholderText="Select date"
              autoComplete="off"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              readOnly={localReadOnly}
              isDisabled={localReadOnly}
            />
          )}
        </Field>
        <ErrorMessage name="lastUpdated" component="div" className="text-danger small" />
      </div>
    </div>
  )
}

export default PersonalDebtsForm
